import { useEffect, useState } from 'react';
import type { FetchCasesResponse, StatusMeta, WorkQueueCase } from '../../services/workQueue';
import type { NextCaseLink } from './NextCaseButton';
import { nextInList, snapshotAction, type NextInList } from './nextInList';
import { filterChips, sortChip, type FilterLine, type ListSort } from './queueListChips';
import { humanizeManufacturerStatus } from './util';
import { metaFor } from './versionedStatusMeta';

// Display values as the toolbar shows them; manufacturer statuses stay raw.
interface QueueFilterValues {
    team: readonly string[];
    assignee: readonly string[];
    priority: readonly string[];
    practice: readonly string[];
    tags: readonly string[];
    monitoring: readonly string[];
    manufacturerStatus: readonly string[];
}

type LoadedCases = Pick<FetchCasesResponse, 'cases' | 'total' | 'page' | 'limit'>;

interface ListSnapshot {
    caseId: string;
    next: NextInList<WorkQueueCase> | null;
    filterChips: string[];
    sortChip: string | null;
}

interface UseNextCaseLinkOptions {
    activeTaskCaseId: string | null;
    continuePending: boolean;
    casesData: LoadedCases | undefined;
    paged: boolean;
    filters: QueueFilterValues;
    sort: ListSort;
    defaultSort: ListSort;
    statusLookup: Map<string, StatusMeta>;
    startTask: (id: string) => void;
    selectCase: (id: string) => void;
    focusCase: (id: string) => void;
}

// Toolbar order, so the chips read like the filter bar.
function filterLines(f: QueueFilterValues): FilterLine[] {
    return [
        { label: 'Team', values: f.team },
        { label: 'Assignee', values: f.assignee },
        { label: 'Priority', values: f.priority },
        { label: 'Practice', values: f.practice },
        { label: 'Tags', values: f.tags },
        { label: 'Monitoring', values: f.monitoring },
        {
            label: 'Manufacturer status',
            values: f.manufacturerStatus.map(humanizeManufacturerStatus)
        }
    ];
}

// Same rule as a row's action button: a step with a task opens it, otherwise the case opens in the queue.
function nextCaseLink(
    snapshot: ListSnapshot,
    { statusLookup, startTask, selectCase, focusCase }: UseNextCaseLinkOptions
): NextCaseLink | undefined {
    if (!snapshot.next) return undefined;
    const { next, remaining } = snapshot.next;
    const hasTask = Boolean(metaFor(statusLookup.get(next.statusId), next.version)?.task);
    const showInQueue = () => {
        selectCase(next.id);
        focusCase(next.id);
    };
    return {
        workCase: next,
        remaining,
        filterChips: snapshot.filterChips,
        sortChip: snapshot.sortChip,
        onOpen: () => (hasTask ? startTask(next.id) : showInQueue())
    };
}

// The list as it stood when the task opened: an advanced case leaves its group and the
// list refetches under it, so later renders can't recompute this.
export function useNextCaseLink(options: UseNextCaseLinkOptions): NextCaseLink | undefined {
    const { activeTaskCaseId, continuePending, casesData, paged, filters, sort, defaultSort } =
        options;
    const [snapshot, setSnapshot] = useState<ListSnapshot | null>(null);
    const action = snapshotAction(
        snapshot?.caseId ?? null,
        activeTaskCaseId,
        continuePending,
        casesData !== undefined
    );

    useEffect(() => {
        if (action === 'clear') setSnapshot(null);
        if (action !== 'rebuild' || !activeTaskCaseId || !casesData) return;
        const rowsBefore = paged ? (casesData.page - 1) * casesData.limit : 0;
        setSnapshot({
            caseId: activeTaskCaseId,
            next: nextInList(casesData.cases, activeTaskCaseId, rowsBefore, casesData.total),
            filterChips: filterChips(filterLines(filters)),
            sortChip: sortChip(sort, defaultSort)
        });
    }, [action, activeTaskCaseId, casesData, paged, filters, sort, defaultSort]);

    // Until the effect catches up, a snapshot from the previous task must not leak into this one.
    if (!snapshot || snapshot.caseId !== activeTaskCaseId) return undefined;
    return nextCaseLink(snapshot, options);
}
